<template>
	<div class="comment-box d-flex flex-column justify-content-start" :class="getBoxClass">
		<div class="d-flex flex-column ga-4">
			<div v-if="(!isMobile || !isReplyBoxOpen) && !hideHeader" class="d-flex align-center justify-space-between">
				<!-- always show on DT -->
				<v-card-title class="pa-0 d-flex align-baseline">
					<div class="d-flex media-header">
						<v-icon>icon-chat-stroke</v-icon>
						<span v-test="'comments-header'" class="heading">Comments ({{ showCount }})</span>
					</div>
				</v-card-title>
				<v-btn v-if="isMobile" icon @click="confirmCloseCommentBox">
					<v-icon>icon-close-large</v-icon>
				</v-btn>
			</div>
			<AddComment
				v-if="canAddComment"
				ref="addCommentRef"
				:project-id="projectId"
				:project-share-users="projectShareUsers"
				:class="''"
				:light-box-view="lightBoxView"
				:users="users"
				@update:addComment="addComment"
				@hide-lucky="hideLucky"
				@show-lucky="showLucky"
				@comment-edited="onCommentEdited"
				@new-comment-edited="onNewCommentEdited" />
		</div>
		<div ref="commentsContainer" class="comments-container" :class="getContainerClass">
			<NoCommentView
				v-if="showNoCommentView"
				:is-view-only="isViewOnly"
				:is-unauthenticated="!isAuthenticated"
				:has-free-trial-ended="hasFreeTrialEnded"
				:light-box-view="lightBoxView"
				:parent-type="parentType" />
			<CommentsList
				v-else-if="comments.length > 0"
				:comments="comments"
				:is-view-only="isViewOnly"
				:project-share-users="projectShareUsers"
				:allow-more="allowMore"
				:light-box-view="lightBoxView"
				:users="users"
				:me="me"
				@update-comment="updateComment"
				@reduce-comment-count="reduceCommentCount"
				@load-more-comments="loadMoreComments"
				@edit-comment="editComment"
				@delete-comment="deleteComment"
				@show-lucky="showLucky"
				@hide-lucky="hideLucky"
				@comment-edited="onCommentEdited"
				@toggle-reply-box="toggleReplyBox" />
			<div v-else class="d-flex justify-center" :class="{ 'loading-indicator-container': lightBoxView }">
				<v-progress-circular indeterminate color="primary" class="loading-indicator"></v-progress-circular>
			</div>
		</div>
	</div>
</template>

<script>
	import AddComment from "./AddComment.vue";
	import CommentsList from "./CommentsList.vue";
	import NoCommentView from "./NoCommentView.vue";
	import api from "@/api";
	import { loadProjectShareRoster } from "@/helpers/projectShareRoster";
	import { isRoleOrInRoles } from "@/api/account";
	import EventBus from "@/helpers/eventBus";
	import { AccessType } from "@/enums/AccessType";
	import { Role } from "@/enums/Role";
	import { getMessageFromError, calculateTimeout, isProjectAccessDeniedError } from "@/helpers/utilities";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import { useOrientationChange } from "@/composables/useOrientationChange";
	import { useCommentsStore } from "@/stores/comments";
	import { confirmDiscardChanges } from "@/composables/useUnsavedChangesGuard";

	/**
	 * LSP-19492: the comment box used to ask "Are you sure you want to lose your changes?"
	 * with a red, filled "Discard changes" as the primary button — the destructive choice
	 * leading the dialog. Closing the box cannot post the draft, so this routes through the
	 * shared two-way discard prompt (`confirmDiscardChanges`), which renders the discard
	 * action as an outlined secondary button and keeps "Keep editing" as the safe default.
	 *
	 * Existing-comment edits keep that copy. A newly typed comment is tracked independently and
	 * uses the standard Don't save / Cancel prompt before a route change can discard it.
	 */
	const UNSAVED_COMMENT_DIALOG_TEXT = {
		title: "You have unsaved changes",
		message: "Your edits to this comment haven't been saved. Would you like to close without saving them?",
		agreeText: "Discard changes",
		cancelText: "Keep editing"
	};

	const UNSENT_COMMENT_DIALOG_TEXT = {
		title: "You have unsaved changes",
		message: "Your comment hasn't been sent. Would you like to leave without sending it?",
		agreeText: "Don't save",
		cancelText: "Cancel"
	};

	export default {
		components: {
			AddComment,
			CommentsList,
			NoCommentView
		},
		setup() {
			const display = useDisplay();
			const toast = useToast();
			const { orientationChangeCount } = useOrientationChange();
			const commentsStore = useCommentsStore();
			return { display, toast, orientationChangeCount, commentsStore };
		},
		props: {
			parentId: {
				type: String,
				required: true
			},
			parentType: {
				type: String,
				required: true
			},
			lightBoxView: {
				type: Boolean,
				default: false
			},
			isViewOnly: {
				type: Boolean,
				default: false
			},
			conversationId: {
				type: String,
				default: null
			},
			projectId: {
				type: String,
				default: null
			},
			projectAccessDeniedHandledGlobally: {
				type: Boolean,
				default: false
			},
			shareId: {
				type: String,
				default: ""
			},
			// LSP-20532: the project-share roster route is owner-organization scoped. A parent that already
			// knows the viewer is an external collaborator (EntityComments) passes false so the box does not
			// issue a request the API is guaranteed to refuse with 404.
			loadProjectShares: {
				type: Boolean,
				default: true
			},
			users: {
				type: Array,
				default: () => [],
				required: true
			},
			me: {
				type: Object,
				required: false
			},
			hasFreeTrialEnded: {
				type: Boolean,
				required: false
			},
			hideHeader: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				// comments / commentCount / showCount / currentCount / currentPages now live
				// in the shared comments store (single source of truth keyed by parentId) so
				// the lightbox's two CommentBox instances share one list and a comment posted
				// in either orientation survives rotation (LSP-16440). Only view-local state
				// remains here.
				loadingComments: true,
				isReplyBoxOpen: false,
				isEditing: false,
				fullHeight: 0,
				isAuthenticated: false,
				hasEditedComment: false,
				hasUnsentComment: false,
				projectShares: null,
				projectCreatorUser: null,
				// Same-organization administrators hold derived EDITOR access without owning a
				// ProjectShare row, so CommentBox derives them for both project comments and the lightbox.
				organizationAdministratorUsers: [],
				// False only while a transient getProject failure is still unresolved — refreshProjectShares
				// retries loadDerivedProjectMentionUsers() when this is false, since it is otherwise the
				// only signal in this component's lifecycle that data may be stale (LSP-20422 review).
				derivedProjectMentionUsersLoaded: false,
				// Set in beforeUnmount and rechecked after every await in the mention-roster loaders below,
				// so closing the lightbox/comments panel mid-request can't write projectCreatorUser,
				// organizationAdministratorUsers, or derivedProjectMentionUsersLoaded onto a torn-down
				// instance (Cursor Bugbot review, LSP-20422).
				isUnmounted: false,
				// Incremented at the start of every loadDerivedProjectMentionUsers() call; each write in
				// that call and its sub-loaders is guarded on still being the current generation, so a
				// slower mount-time load cannot overwrite a fresher refreshProjectShares-triggered one
				// that started later but resolved first — mirrors ShareActionDialog.vue's loadGeneration
				// (AI review, LSP-20422).
				derivedRosterLoadGeneration: 0
			};
		},
		computed: {
			// Store-backed comment state, keyed by parent type and id. Reading byParent[key]
			// (rather than store.entry(), which creates) keeps these getters side-effect free
			// and still reactive when the entry is first created by loadComments.
			commentEntry() {
				return (
					this.commentsStore.byParent[this.commentStoreKey] || {
						comments: [],
						commentCount: 0,
						showCount: 0,
						currentCount: 0,
						currentPages: 1,
						loaded: false
					}
				);
			},
			commentStoreKey() {
				return this.commentsStore.key(this.parentId, this.parentType);
			},
			comments() {
				return this.commentEntry.comments;
			},
			commentCount() {
				return this.commentEntry.commentCount;
			},
			showCount() {
				return this.commentEntry.showCount;
			},
			currentCount() {
				return this.commentEntry.currentCount;
			},
			isMobile() {
				return this.display.smAndDown.value;
			},
			projectShareUsers() {
				const users = [
					...(this.projectCreatorUser ? [this.projectCreatorUser] : []),
					...this.organizationAdministratorUsers,
					...(this.projectShares?.rows
						?.filter((share) => share.accessType !== AccessType.Viewer && share.User)
						.map((share) => share.User) || [])
				];
				const uniqueUsers = new Map();
				for (const user of users) {
					if (!uniqueUsers.has(user.id)) uniqueUsers.set(user.id, user);
				}
				return Array.from(uniqueUsers.values());
			},
			canAddComment() {
				return !this.isReplyBoxOpen && !this.isViewOnly && this.isAuthenticated;
			},
			allowMore() {
				return this.commentCount > this.currentCount;
			},
			showNoCommentView() {
				// If there are no comments, not authenticated, or no me
				return (this.comments.length === 0 && !this.loadingComments) || !this.isAuthenticated || !this.me;
			},
			getBoxClass() {
				return {
					"light-box-comments-box": this.lightBoxView
				};
			},
			getContainerClass() {
				return {
					"comments-container-no-scroll": this.isReplyBoxOpen && (this.isMobile || this.lightBoxView),
					"light-box-comments-container": this.lightBoxView || this.isMobile
				};
			}
		},
		watch: {
			commentStoreKey: async function () {
				await this.loadComments();
				this.$refs.addCommentRef?.clearComment();
				this.isReplyBoxOpen = false;
				this.isEditing = false;
			},
			async orientationChangeCount() {
				// LSP-16440 root cause: on an iPad rotation the media lightbox swaps to a
				// DIFFERENT CommentBox instance whose list was loaded before the newest
				// comment was posted, so a just-posted (already-persisted) comment is missing
				// until a manual refresh. Re-read the server — the single source of truth — so
				// the comment reappears on rotation. Reading the server (whole-list replace in
				// loadComments) carries no client-cache dedupe/double-comment risk.
				try {
					await this.loadComments();
				} catch (error) {
					console.error("Error reloading comments after orientation change", error);
				}
				// New comments are prepended (newest at the top), so reveal the top after reflow.
				this.$nextTick(() => {
					const commentsContainer = this.$refs.commentsContainer;
					if (commentsContainer) {
						commentsContainer.scrollTop = 0;
					}
				});
			}
		},
		async mounted() {
			this._onProjectShareAccessChanged = async (_item, projectId, _accessType) => {
				if (projectId === this.projectId) {
					await this.refreshProjectShares();
				}
			};
			EventBus.$on("ProjectShareAccessChanged", this._onProjectShareAccessChanged);

			try {
				this.isAuthenticated = await this.$auth.isAuthenticated();
				if (this.isAuthenticated) {
					// LSP-20532: 404 (project owned by another organization) resolves to an empty roster.
					// loadDerivedProjectMentionUsers is independent of loadProjectShares — the creator/org-admin
					// fetch has its own same-organization gate (LSP-20422) and is still meaningful for an
					// external collaborator who is not fetching the (would-404) project-share roster.
					[this.projectShares] = await Promise.all([
						this.loadProjectShares ? loadProjectShareRoster(this.projectId) : Promise.resolve({ rows: [] }),
						this.loadDerivedProjectMentionUsers()
					]);
				} else {
					this.projectShares = { rows: [] };
				}
				this.fullHeight = window.innerHeight;
			} catch (error) {
				console.error("Error getting project shares", error);
			}
			try {
				await this.loadComments();
			} catch (error) {
				console.error("Error loading comments", error);
			}
		},
		beforeUnmount() {
			this.isUnmounted = true;
			if (this._onProjectShareAccessChanged) {
				EventBus.$off("ProjectShareAccessChanged", this._onProjectShareAccessChanged);
			}
		},
		methods: {
			// Mention-roster members that no longer own ProjectShare rows after the sharing fan-out
			// was removed: the project creator and the owning organization's administrators.
			async loadDerivedProjectMentionUsers() {
				const myGeneration = ++this.derivedRosterLoadGeneration;
				if (!this.isAuthenticated || !this.projectId) {
					console.debug("Comment mention roster: derived project users not loaded for a non-project or unauthenticated context", {
						projectId: this.projectId,
						parentId: this.parentId,
						parentType: this.parentType,
						isAuthenticated: this.isAuthenticated
					});
					return;
				}

				let project = null;
				try {
					project = await api.getProject?.(this.projectId);
				} catch (error) {
					// Leave derivedProjectMentionUsersLoaded false: this is the one failure mode in this
					// method worth retrying (a transient fetch failure, not a real "no such project"),
					// and refreshProjectShares (fired on ProjectShareAccessChanged) is this component's
					// only other trigger to try again — remounting is otherwise required (LSP-20422 review).
					console.error("Error getting project for the comment mention roster", error, {
						projectId: this.projectId,
						parentId: this.parentId,
						parentType: this.parentType
					});
					return;
				}

				if (this.isUnmounted || myGeneration !== this.derivedRosterLoadGeneration) return;

				if (!project) {
					console.warn("Comment mention roster: no project record; creator and organization administrators are unavailable", {
						projectId: this.projectId,
						parentId: this.parentId,
						parentType: this.parentType
					});
					this.derivedProjectMentionUsersLoaded = true;
					return;
				}

				const [creatorLoaded, administratorsLoaded] = await Promise.all([
					this.loadProjectCreator(project, myGeneration),
					this.loadOrganizationAdministrators(project, myGeneration)
				]);
				if (this.isUnmounted || myGeneration !== this.derivedRosterLoadGeneration) return;
				// Both sub-loaders catch their own errors and never reject, so Promise.all above always
				// resolves regardless of whether either fetch actually succeeded. Only mark the roster
				// loaded when both genuinely completed — a transient failure in either leg must leave
				// this false so refreshProjectShares' retry (the component's only other re-entry point,
				// per the comment on this flag's declaration) still fires (AI review, LSP-20422).
				this.derivedProjectMentionUsersLoaded = creatorLoaded && administratorsLoaded;
			},
			// Returns false only for a transient fetch failure worth retrying; every other outcome
			// (including a legitimate empty result) returns true.
			async loadProjectCreator(project, myGeneration) {
				try {
					const usedDirectoryFallback = !project?.User && !!project?.createdBy;
					const creatorResponse = project?.User
						? { rows: [project.User] }
						: project?.createdBy
							? await api.getUsers({
									page: 1,
									pageSize: 1,
									where: { id: project.createdBy }
								})
							: { rows: [] };
					if (this.isUnmounted || myGeneration !== this.derivedRosterLoadGeneration) return true;
					this.projectCreatorUser = creatorResponse.rows[0] || null;
					if (usedDirectoryFallback && !this.projectCreatorUser) {
						console.warn("Comment mention roster: project creator directory fallback returned no user", {
							projectId: this.projectId,
							parentId: this.parentId,
							parentType: this.parentType,
							createdBy: project.createdBy
						});
					}
					return true;
				} catch (error) {
					// The only thing in the try block that can reject is the getUsers directory
					// fallback (project?.User access and object literals above cannot throw), so
					// reaching here always means the fallback failed — most commonly a 403 for a
					// View-Only caller, who lacks READ_USER (LSP-20422 review). Warn distinctly from
					// the error log so an operator debugging "creator missing from mentions" has a
					// breadcrumb naming the specific, expected-to-happen cause.
					console.warn("Comment mention roster: project creator directory fallback failed; creator unavailable", {
						projectId: this.projectId,
						parentId: this.parentId,
						parentType: this.parentType,
						createdBy: project?.createdBy
					});
					console.error("Error getting project creator for the comment mention roster", error, {
						projectId: this.projectId,
						parentId: this.parentId,
						parentType: this.parentType
					});
					// A 403 (View-Only, lacking READ_USER) is a permanent, not transient, outcome —
					// retrying it on every ProjectShareAccessChanged event would never succeed, so only
					// a genuinely transient failure should block derivedProjectMentionUsersLoaded. This
					// method cannot tell the two apart from the error alone, so it conservatively treats
					// the failure as retryable — worst case, a few harmless extra retries.
					return false;
				}
			},
			// Returns false only for a transient fetch failure worth retrying; every other outcome
			// (including a legitimate empty result) returns true.
			async loadOrganizationAdministrators(project, myGeneration) {
				const callerOrganizationId = await this.resolveCallerOrganizationId();
				if (this.isUnmounted || myGeneration !== this.derivedRosterLoadGeneration) return true;
				const projectOrganizationId = project?.organizationId;
				if (!callerOrganizationId || !projectOrganizationId || projectOrganizationId !== callerOrganizationId) {
					this.organizationAdministratorUsers = [];
					console.warn("Comment mention roster: same-organization ownership not proven; organization administrators excluded", {
						projectId: this.projectId,
						callerOrganizationId,
						projectOrganizationId
					});
					return true;
				}

				try {
					const sharingUsers = await api.getSharingUsers();
					if (this.isUnmounted || myGeneration !== this.derivedRosterLoadGeneration) return true;
					if (!Array.isArray(sharingUsers)) {
						console.warn(
							"Comment mention roster: getSharingUsers returned a non-array response; organization administrators unavailable",
							{
								projectId: this.projectId,
								parentId: this.parentId,
								parentType: this.parentType
							}
						);
						this.organizationAdministratorUsers = [];
						return true;
					}
					this.organizationAdministratorUsers = sharingUsers.filter((user) =>
						user?.Accounts?.[0]?.Roles?.some((role) => isRoleOrInRoles(role?.name, Role.Administrator))
					);
					return true;
				} catch (error) {
					console.error("Error getting organization administrators for the comment mention roster", error, {
						projectId: this.projectId,
						parentId: this.parentId,
						parentType: this.parentType
					});
					if (this.isUnmounted || myGeneration !== this.derivedRosterLoadGeneration) return true;
					this.organizationAdministratorUsers = [];
					return false;
				}
			},
			async resolveCallerOrganizationId() {
				const organizationIdFromProp = this.me?.organization?.id;
				if (organizationIdFromProp) return organizationIdFromProp;
				try {
					const me = await api.getMe();
					return me?.organization?.id;
				} catch (error) {
					console.error("Error resolving caller organization for the comment mention roster", error);
					return undefined;
				}
			},
			async refreshProjectShares() {
				if (!this.isAuthenticated) return;
				if (this.loadProjectShares) {
					try {
						this.projectShares = await loadProjectShareRoster(this.projectId);
					} catch (error) {
						console.error("Error refreshing project shares", error);
					}
				}
				// Retry the derived creator/org-admin roster if its initial load never completed (a
				// transient getProject failure at mount) — this event is the component's only other
				// natural re-entry point, so use it rather than leaving the roster stuck until remount.
				// Independent of loadProjectShares, matching mounted()'s treatment of the derived roster
				// as unrelated to project-share-roster visibility (LSP-20422 review).
				if (!this.derivedProjectMentionUsersLoaded) {
					await this.loadDerivedProjectMentionUsers();
				}
			},
			async confirmCloseCommentBox() {
				if (await this.confirmLeaveWithUnsavedComment()) {
					this.closeCommentBox();
				}
			},
			async confirmLeaveWithUnsavedComment() {
				if (!this.hasEditedComment && !this.hasUnsentComment) return true;
				const dialogText = this.hasUnsentComment ? UNSENT_COMMENT_DIALOG_TEXT : UNSAVED_COMMENT_DIALOG_TEXT;
				const discard = await confirmDiscardChanges(this, dialogText);
				if (discard) return true;
				// `null` means the confirm dialog was unavailable (no $root.$confirm host) as well as
				// cancellation, and both must block navigation. Log the unavailable case so a future
				// "navigation did nothing" report does not have to be reconstructed indirectly.
				if (discard === null && !this.$root?.$confirm) {
					console.warn(
						`[CommentBox] close blocked: confirm dialog host unavailable. parentType=${this.parentType}, parentId=${this.parentId}, projectId=${this.projectId}`
					);
				}
				return false;
			},
			closeCommentBox() {
				this.$emit("close-comment-box");
				if (this.isReplyBoxOpen) {
					EventBus.$emit("CloseReplies");
				}
			},
			reduceCommentCount() {
				this.commentsStore.decrementShowCount(this.parentId, this.parentType);
			},
			async loadMoreComments() {
				this.commentsStore.incrementPages(this.parentId, this.parentType);
				await this.loadComments();
			},
			async loadComments(comment) {
				if (!this.me) {
					this.loadingComments = false;
					return;
				}
				// A just-posted comment (already persisted, carries a server id): prepend it
				// into the shared store so it is instantly visible to every CommentBox bound to
				// this parentId — including the lightbox's other-orientation instance.
				if (comment) {
					this.commentsStore.prependPosted(this.parentId, comment, this.parentType);
					this.loadingComments = false;
					return;
				}
				// Full reload from the server. The store replaces the list wholesale (single
				// source of truth → idempotent by id → no duplicates), and existing comments
				// stay visible while the request is in flight.
				this.loadingComments = true;
				await this.commentsStore.loadEntry(this.parentId, this.isAuthenticated, this.parentType, this.projectId, this.shareId);
				this.loadingComments = false;
			},
			async updateComment(comment) {
				this.commentsStore.applyUpdate(this.parentId, comment, this.parentType);
			},
			async editComment(comment, isEditing) {
				this.isEditing = isEditing;
			},
			toggleReplyBox(isOpen, commentId) {
				this.isReplyBoxOpen = isOpen;
				this.$emit("toggle-reply-box", isOpen, commentId);
			},
			async deleteComment() {
				await this.loadComments();
			},
			async addComment(comment) {
				try {
					const body = {
						parentId: this.parentId,
						parentType: this.parentType,
						content: comment
					};
					if (this.conversationId) {
						body.conversationId = this.conversationId;
					}
					const commentData = await api.createComment(body);
					await this.loadComments(commentData);
				} catch (error) {
					if (isProjectAccessDeniedError(error) && this.projectAccessDeniedHandledGlobally) return;
					const message = getMessageFromError(error);
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
			},
			hideLucky() {
				this.$emit("hide-lucky");
			},
			showLucky() {
				this.$emit("show-lucky");
			},
			onCommentEdited(editedComment) {
				this.hasEditedComment = editedComment;
			},
			onNewCommentEdited(editedComment) {
				this.hasUnsentComment = editedComment;
			}
		}
	};
</script>

<style scoped>
	.media-header {
		align-items: center;
		margin-bottom: 0px;
		color: rgb(var(--v-theme-ink));
	}

	.media-header .v-icon {
		width: 28px;
		height: 28px;
		color: rgb(var(--v-theme-brand));
		margin-right: 8px;
		padding: 6px;
	}

	.loading-indicator {
		width: 32px;
		height: 32px;
	}

	.loading-indicator-container {
		position: absolute;
		top: calc(50% - 32px);
		left: calc(50% - 32px);
	}

	.light-box-comments-box {
		height: 100%;
	}

	.comments-container-no-scroll {
		overflow: hidden !important;
	}

	.light-box-comments-container {
		overflow-y: auto !important;
		scrollbar-width: thin;
		min-height: 0;
		flex: 1;
	}

	@media (max-width: 959px) {
		.comment-box {
			padding: 16px 8px !important;
		}
	}
</style>
